import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { FixedSizeList, ListChildComponentProps } from "react-window";

import { useAsu, IMenuTreeItem, IFoundTrain } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { EquipmentStateDisplayMode, IUserDto, useUser } from "@framework/provider-user";

import Factory from "../../../../../assets/icons/factory.svg";
import Train from "../../../../../assets/icons/train.svg";
import { useAlarmPaletteResolver } from "../../../../../hooks/use-alarm-color";
import {
  collectSearchStatusRequests,
  useMenuStatusSubscriptions,
} from "../../../../../pages/subscribe/use-menu-status-subscriptions";
import { StyledTreeItem } from "../tree-item";
import { StyledEmptyWrapper, StyledLabelWrapper, StyledTreeView } from "./styled";
import { resolveStationIconColor, resolveTrainIconColor } from "../icon-color";

export interface ISearchTreeView {
  onSelect?: () => void;
}

type SearchStation = IMenuTreeItem & { parentNames: string[] };
type SearchItem = SearchStation | IFoundTrain;

interface ISearchResultListData {
  items: SearchItem[];
  renderItem: (item: SearchItem) => JSX.Element | null;
}

const SEARCH_RESULT_ITEM_HEIGHT = 52;
const SEARCH_RESULT_MAX_HEIGHT_OFFSET = 238;
const SEARCH_RESULT_OVERSCAN_COUNT = 8;
const SEARCH_RESULT_TEXT_SX = {
  fontWeight: "500",
  fontSize: 16,
  lineHeight: "19px",
  flexGrow: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const SEARCH_RESULT_PATH_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const getMaxSearchResultListHeight = (): number => {
  if (typeof window === "undefined") {
    return SEARCH_RESULT_ITEM_HEIGHT;
  }

  return Math.max(SEARCH_RESULT_ITEM_HEIGHT, window.innerHeight - SEARCH_RESULT_MAX_HEIGHT_OFFSET);
};

const getSearchItemKey = (index: number, data: ISearchResultListData): string => {
  const item = data.items[index];

  if ("TrainId" in item && item.TrainId) {
    return `train-${item.TrainId}`;
  }

  if ("Id" in item && item.Id) {
    return `station-${item.Id}`;
  }

  return `item-${index}`;
};

const SearchResultRow = ({ data, index, style }: ListChildComponentProps<ISearchResultListData>) => (
  <Box style={style} sx={{ boxSizing: "border-box" }}>
    {data.renderItem(data.items[index])}
  </Box>
);

export const SearchTreeView: FC<ISearchTreeView> = props => {
  const { onSelect = () => {} } = props;
  const listRef = useRef<FixedSizeList<ISearchResultListData> | null>(null);
  const [maxListHeight, setMaxListHeight] = useState<number>(getMaxSearchResultListHeight);
  const [visibleRange, setVisibleRange] = useState({ start: 0, stop: -1 });
  const {
    selectStation,
    searchList: items,
    resetSearchList,
    selectTrain,
    selectedItem,
    isVisualControlOpen,
  } = useAsu();
  const settings = useSettings();
  const user = useUser<IUserDto>();
  const equipmentStateDisplayMode =
    user.profile?.interfaceSettings?.equipmentStateDisplayMode ?? EquipmentStateDisplayMode.GOST;
  const tciLevels = settings.getTCILevels();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const itemCount = items?.length || 0;
  const listHeight = Math.min(itemCount * SEARCH_RESULT_ITEM_HEIGHT, maxListHeight);
  const visibleItems = useMemo(
    () => items?.slice(visibleRange.start, visibleRange.stop + 1) ?? [],
    [items, visibleRange.start, visibleRange.stop],
  );
  const visibleStatusRequests = useMemo(() => collectSearchStatusRequests(visibleItems), [visibleItems]);
  useMenuStatusSubscriptions(visibleStatusRequests);

  const handleSelectStation = useCallback(
    (stationId: number) => {
      selectStation(stationId);
      resetSearchList();
      onSelect();
    },
    [onSelect, resetSearchList, selectStation],
  );

  const handleSelectTrain = useCallback(
    (trainId: number) => {
      selectTrain(trainId);
      resetSearchList();
      onSelect();
    },
    [onSelect, resetSearchList, selectTrain],
  );

  const renderTrain = useCallback(
    (train: IFoundTrain) => {
      const { TrainId, TrainName, ParentsNames } = train;

      return TrainId && TrainName ? (
        <StyledTreeItem
          key={`${TrainId}_${TrainName}`}
          nodeId={`train-${TrainId}`}
          startIcon={<Train />}
          width={14}
          height={15}
          isSelected={!!selectedItem && "TrainId" in selectedItem && selectedItem.TrainId === TrainId}
          startIconColor={resolveTrainIconColor(train, {
            equipmentStateDisplayMode,
            isVisualControlOpen,
            tciLevels,
          })}
          onClick={() => handleSelectTrain(TrainId)}
          label={
            <StyledLabelWrapper>
              <Typography sx={SEARCH_RESULT_TEXT_SX}>{TrainName || TrainId}</Typography>
              {ParentsNames && ParentsNames.length ? (
                <Typography variant="caption" color="#AEAEB8" sx={SEARCH_RESULT_PATH_SX}>
                  {ParentsNames.join("/")}
                </Typography>
              ) : null}
            </StyledLabelWrapper>
          }
        />
      ) : null;
    },
    [equipmentStateDisplayMode, handleSelectTrain, isVisualControlOpen, selectedItem, tciLevels],
  );

  const renderStation = useCallback(
    (station: IMenuTreeItem & { parentNames: string[] }) => {
      const { Id, Name, parentNames } = station;

      return (
        <StyledTreeItem
          key={Id}
          nodeId={`station-${Id}`}
          startIcon={<Factory />}
          width={22}
          height={22}
          startIconColor={resolveStationIconColor(station, {
            equipmentStateDisplayMode,
            isVisualControlOpen,
            tciLevels,
            resolveAlarmColor,
          })}
          isSelected={false}
          onClick={() => handleSelectStation(Id)}
          label={
            <StyledLabelWrapper>
              <Typography sx={SEARCH_RESULT_TEXT_SX}>{Name}</Typography>
              {parentNames && parentNames.length ? (
                <Typography variant="caption" color="#AEAEB8" sx={SEARCH_RESULT_PATH_SX}>
                  {parentNames.join("/")}
                </Typography>
              ) : null}
            </StyledLabelWrapper>
          }
        />
      );
    },
    [equipmentStateDisplayMode, handleSelectStation, isVisualControlOpen, resolveAlarmColor, tciLevels],
  );

  const renderItem = useCallback(
    (item: SearchItem) => {
      if ("TrainId" in item && item.TrainId) {
        return renderTrain(item);
      } else if ("Id" in item && item.Id) {
        return renderStation(item);
      } else {
        return null;
      }
    },
    [renderStation, renderTrain],
  );

  const listData = useMemo<ISearchResultListData>(
    () => ({
      items: items || [],
      renderItem,
    }),
    [items, renderItem],
  );

  useEffect(() => {
    const updateMaxListHeight = () => {
      setMaxListHeight(getMaxSearchResultListHeight());
    };

    window.addEventListener("resize", updateMaxListHeight);

    return () => {
      window.removeEventListener("resize", updateMaxListHeight);
    };
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo(0);
  }, [items]);

  if (!items || !items.length) {
    return (
      <StyledEmptyWrapper>
        <Typography fontSize={18} lineHeight="21px" fontWeight={500}>
          <FormattedMessage id="components.mainMenu.emptySearch" />
        </Typography>
      </StyledEmptyWrapper>
    );
  }

  return (
    <StyledTreeView>
      <FixedSizeList
        ref={listRef}
        height={listHeight}
        itemCount={itemCount}
        itemData={listData}
        itemKey={getSearchItemKey}
        itemSize={SEARCH_RESULT_ITEM_HEIGHT}
        onItemsRendered={({ visibleStartIndex, visibleStopIndex }) => {
          setVisibleRange(current =>
            current.start === visibleStartIndex && current.stop === visibleStopIndex
              ? current
              : { start: visibleStartIndex, stop: visibleStopIndex },
          );
        }}
        overscanCount={SEARCH_RESULT_OVERSCAN_COUNT}
        width="100%"
      >
        {SearchResultRow}
      </FixedSizeList>
    </StyledTreeView>
  );
};
